import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, RefreshCw, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageShell } from "@/components/page";
import { cn } from "@/lib/utils";

type VoiceBackend = "kokoro" | "openai" | "misconfigured";

type User = {
  id: string;
  name: string;
  persona: string;
  subdomain: string;
  url: string;
  created_on: string;
  last_deployment_at: string | null;
  last_deployment_status: string | null;
  latest_deployment_id: string | null;
  voice_backend: VoiceBackend;
};

const VOICE_DOT: Record<VoiceBackend, string> = {
  kokoro:        "bg-emerald-500",
  openai:        "bg-red-500",
  misconfigured: "bg-amber-500",
};

const VOICE_LABEL: Record<VoiceBackend, string> = {
  kokoro:        "Kokoro",
  openai:        "OpenAI",
  misconfigured: "Misconfigured",
};

// Six-tone palette for the persona avatar circle. Deterministic by first char.
const AVATAR_PALETTE: { bg: string; fg: string }[] = [
  { bg: "bg-blue-500/15",    fg: "text-blue-700"    },
  { bg: "bg-emerald-500/15", fg: "text-emerald-700" },
  { bg: "bg-violet-500/15",  fg: "text-violet-700"  },
  { bg: "bg-amber-500/15",   fg: "text-amber-700"   },
  { bg: "bg-rose-500/15",    fg: "text-rose-700"    },
  { bg: "bg-teal-500/15",    fg: "text-teal-700"    },
];

function avatarTone(persona: string): { bg: string; fg: string } {
  const ch = persona.charCodeAt(0) || 0;
  return AVATAR_PALETTE[ch % AVATAR_PALETTE.length];
}

type StatusKey = "success" | "failure" | "canceled" | "queued" | "active" | "unknown";

function statusKey(raw: string | null): StatusKey {
  if (!raw) return "unknown";
  const v = raw.toLowerCase();
  if (v === "success" || v === "succeeded" || v === "deployed") return "success";
  if (v === "failure" || v === "failed" || v === "error") return "failure";
  if (v === "canceled" || v === "cancelled") return "canceled";
  if (v === "queued" || v === "pending") return "queued";
  if (v === "active" || v === "building" || v === "running") return "active";
  return "unknown";
}

const STATUS_DOT: Record<StatusKey, string> = {
  success:  "bg-emerald-500",
  failure:  "bg-red-500",
  canceled: "bg-slate-400",
  queued:   "bg-amber-500",
  active:   "bg-amber-500",
  unknown:  "bg-slate-400",
};

const STATUS_LABEL: Record<StatusKey, string> = {
  success:  "Live",
  failure:  "Failed",
  canceled: "Canceled",
  queued:   "Queued",
  active:   "Building",
  unknown:  "Unknown",
};

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
}

export function UsersPage() {
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api("/api/users");
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status} — ${text || "request failed"}`);
      }
      const data = (await res.json()) as User[];
      setUsers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell
      icon={LayoutDashboard}
      iconColor="violet"
      title="Users"
      subtitle={
        loading
          ? "Loading dashboards…"
          : error
            ? "Couldn't load dashboards"
            : `${users.length} customer dashboard${users.length === 1 ? "" : "s"} on Cloudflare Pages`
      }
      actions={
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          Refresh
        </Button>
      }
    >
      {error ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">Error: {error}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => void load()}
          >
            Retry
          </Button>
        </div>
      ) : loading ? (
        <UsersTableSkeleton />
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card/50 p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/15 text-violet-600">
            <LayoutDashboard className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-medium">No user dashboards found.</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Cloudflare Pages projects matching{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
              my-jarvis-dashboard-*
            </code>{" "}
            will appear here.
          </p>
        </div>
      ) : (
        <UsersTable users={users} />
      )}
    </PageShell>
  );
}

function UsersTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="grid grid-cols-[44px_1fr_1.4fr_1fr_0.8fr_0.9fr_0.8fr_24px] gap-3 border-b bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span />
        <span>Persona</span>
        <span>URL</span>
        <span>Last Deploy</span>
        <span>Sessions</span>
        <span>Voice</span>
        <span>Status</span>
        <span />
      </div>
      <ul className="divide-y">
        {Array.from({ length: 5 }).map((_, i) => (
          <li
            key={i}
            className="grid grid-cols-[44px_1fr_1.4fr_1fr_0.8fr_0.9fr_0.8fr_24px] items-center gap-3 px-4 py-3"
          >
            <span className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <span className="h-3.5 w-24 rounded bg-muted animate-pulse" />
            <span className="h-3.5 w-56 rounded bg-muted animate-pulse" />
            <span className="h-3.5 w-20 rounded bg-muted animate-pulse" />
            <span className="h-3.5 w-10 rounded bg-muted animate-pulse" />
            <span className="h-3.5 w-14 rounded bg-muted animate-pulse" />
            <span className="h-3.5 w-16 rounded bg-muted animate-pulse" />
            <span />
          </li>
        ))}
      </ul>
    </div>
  );
}

function UsersTable({ users }: { users: User[] }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="grid grid-cols-[44px_1fr_1.4fr_1fr_0.8fr_0.9fr_0.8fr_24px] gap-3 border-b bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span />
        <span>Persona</span>
        <span>URL</span>
        <span>Last Deploy</span>
        <span>Sessions</span>
        <span>Voice</span>
        <span>Status</span>
        <span />
      </div>
      <ul className="divide-y">
        {users.map((u) => {
          const tone = avatarTone(u.persona);
          const sk = statusKey(u.last_deployment_status);
          return (
            <li key={u.id}>
              <Link
                to={`/users/${u.persona}`}
                className="grid w-full grid-cols-[44px_1fr_1.4fr_1fr_0.8fr_0.9fr_0.8fr_24px] items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-muted/50 cursor-pointer"
              >
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    tone.bg,
                    tone.fg,
                  )}
                  aria-hidden
                >
                  {(u.persona[0] ?? "?").toUpperCase()}
                </span>

                <span className="font-medium capitalize text-foreground">
                  {u.persona}
                </span>

                <span className="truncate text-muted-foreground">
                  {u.subdomain}
                </span>

                <span className="text-xs text-muted-foreground">
                  {relativeTime(u.last_deployment_at)}
                </span>

                <span className="text-xs text-muted-foreground">—</span>

                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full", VOICE_DOT[u.voice_backend])}
                  />
                  {VOICE_LABEL[u.voice_backend]}
                </span>

                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[sk])}
                  />
                  {STATUS_LABEL[sk]}
                </span>

                <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
